import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Store, Users } from "lucide-react";
import api from "../../lib/api";
import { Label } from "../../components/common";

function Medal({ rank }) {
  if (rank === 1) return <span title="#1" className="text-xl">🥇</span>;
  if (rank === 2) return <span title="#2" className="text-xl">🥈</span>;
  if (rank === 3) return <span title="#3" className="text-xl">🥉</span>;
  return <span className="font-mono text-sm font-bold text-slate-500">#{rank}</span>;
}

export default function StoreKPI() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/store-kpi/leaderboard?days=${days}`).then((r) => setData(r.data)).catch(() => setData({ locations: [] }));
  }, [days]);

  if (!data) return <div className="text-slate-400">Loading…</div>;

  const noData = (data.locations || []).length === 0;

  return (
    <div className="space-y-6" data-testid="store-kpi-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Label>Store KPI Ranking</Label>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">How your stores rank</h1>
          <p className="mt-1 text-sm text-slate-500 max-w-xl">
            Ranked by the <span className="font-semibold text-amber-700">store-scope</span> criteria you set up in
            Rating Form &amp; Recovery. Waiter SSI is unaffected.
          </p>
        </div>
        <div className="flex gap-1 rounded-full border border-slate-200 bg-white p-1 text-xs" data-testid="kpi-days">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`kpi-days-${d}`}
              className={`rounded-full px-3 py-1 font-semibold transition-colors ${
                days === d ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100"
              }`}>Last {d}d</button>
          ))}
        </div>
      </div>

      {noData ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="kpi-empty">
          <Store className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 font-head text-base font-bold text-slate-800">No store KPI data yet</p>
          <p className="mt-1 text-sm text-slate-500">
            Set at least one dimension or question as <span className="font-semibold text-amber-700">Store</span>{" "}
            in Rating Form &amp; Recovery and wait for customer ratings to come in.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Rank</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3 text-right">Store KPI</th>
                <th className="hidden px-4 py-3 text-right sm:table-cell">Sample</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.locations.map((row) => (
                <tr key={row.location_id} data-testid={`kpi-row-${row.location_id}`}
                  onClick={() => navigate(`/app/store-kpi/${row.location_id}`)}
                  className="cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-3"><Medal rank={row.rank} /></td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{row.location_name}</div>
                    {row.city && <div className="text-xs text-slate-500">{row.city}</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-head text-lg font-bold text-amber-700">{row.overall ?? "–"}</span>
                    <span className="text-xs text-slate-400">/100</span>
                  </td>
                  <td className="hidden px-4 py-3 text-right text-xs text-slate-500 sm:table-cell">{row.sample}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-500" data-testid="kpi-note">
        <div className="flex items-start gap-2">
          <Users className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-500" />
          <p>
            <span className="font-semibold text-slate-700">Independent ranking.</span> Waiters' SSI only uses
            waiter-scope criteria and their individual star rating. Store KPI aggregates every store-scope criterion
            across the location's recent feedback.
          </p>
        </div>
      </div>
    </div>
  );
}
